import { useEffect, useState } from "react";
import { FilterValues } from "../../../data-model/filterValues";
import { buildNycbsExternalFilterPayload } from "../utils/buildNycbsExternalFilterPayload";
import { NycbsExternalFilterPayload } from "../utils/nycbsExternalFilterPayload";

export type { NycbsExternalFilterPayload };

export interface UseFilterPayloadReturn {
  filterPayload: NycbsExternalFilterPayload | null;
  setFilterPayload: React.Dispatch<
    React.SetStateAction<NycbsExternalFilterPayload | null>
  >;
}

/**
 * Builds and stores the filter/search API payload when filters or search change.
 *
 * @param validFilters - Active sidebar filters for this table
 * @param searchTerm - Current search text
 * @param initialDataFetchingExecuted - Whether the initial fetch has completed
 * @param dateFromStart - Default range start date (YYYYMMDD)
 * @param dateFromEnd - Default range end date (YYYYMMDD)
 * @param pageSize - Number of rows per page
 * @param orgId - BigQuery organization id (Non-STAT or STAT)
 * @return UseFilterPayloadReturn: Current payload and setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
  orgId: string,
): UseFilterPayloadReturn => {
  const [filterPayload, setFilterPayload] =
    useState<NycbsExternalFilterPayload | null>(null);

  useEffect(() => {
    if (!initialDataFetchingExecuted) {
      setFilterPayload(null);
      return;
    }

    setFilterPayload(
      buildNycbsExternalFilterPayload(
        validFilters,
        searchTerm,
        dateFromStart,
        dateFromEnd,
        pageSize,
        orgId,
      ),
    );
  }, [
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
    orgId,
  ]);

  return {
    filterPayload,
    setFilterPayload,
  };
};
